import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Lock, Unlock, Shield, User } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

// ========================================
// POS LOCK SCREEN COMPONENT
// Écran de verrouillage de la caisse
// Version corrigée - z-index, pointer-events, persistance
// ========================================

const POSLockScreen = ({ 
    isLocked, 
    onUnlock, 
    user, 
    isAdmin,
    lockedByInactivity = false 
}) => {
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showAdminBypass, setShowAdminBypass] = useState(false);
    const containerRef = useRef(null);
    
    // PIN length based on user role
    const requiredPinLength = user?.role === 'ADMIN' ? 6 : 4;

    // Reset PIN when lock screen appears
    useEffect(() => {
        if (isLocked) {
            setPin('');
            setError('');
            setShowAdminBypass(false);
            // Focus sur le conteneur pour capturer les événements clavier
            setTimeout(() => {
                containerRef.current?.focus();
            }, 100);
        }
    }, [isLocked]);

    // Bloquer le scroll du body quand verrouillé
    useEffect(() => {
        if (isLocked) {
            document.body.style.overflow = 'hidden';
            document.body.style.pointerEvents = 'none';
        } else {
            document.body.style.overflow = '';
            document.body.style.pointerEvents = '';
        }
        return () => {
            document.body.style.overflow = '';
            document.body.style.pointerEvents = '';
        };
    }, [isLocked]);

    // Handle PIN digit input
    const handleDigitClick = useCallback((digit) => {
        if (pin.length < 6) {
            setPin(prev => prev + digit);
            setError('');
        }
    }, [pin.length]);

    // Handle backspace
    const handleBackspace = useCallback(() => {
        setPin(prev => prev.slice(0, -1));
        setError('');
    }, []);

    // Handle clear
    const handleClear = useCallback(() => {
        setPin('');
        setError('');
    }, []);

    // Verify PIN and unlock
    const handleUnlock = useCallback(async () => {
        if (pin.length < requiredPinLength) {
            setError(`PIN trop court (${requiredPinLength} chiffres requis)`);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const success = await onUnlock(pin, false);
            if (!success) {
                setError('PIN incorrect');
                setPin('');
            }
        } catch (err) {
            setError('Erreur de vérification');
            setPin('');
        } finally {
            setLoading(false);
        }
    }, [pin, onUnlock, requiredPinLength]);

    // Admin bypass
    const handleAdminBypass = useCallback(async () => {
        if (!isAdmin) return;
        
        setLoading(true);
        try {
            await onUnlock('', true);
        } catch (err) {
            setError('Erreur');
        } finally {
            setLoading(false);
        }
    }, [isAdmin, onUnlock]);

    // Auto-submit when PIN is complete
    useEffect(() => {
        if (pin.length === requiredPinLength && !loading) {
            handleUnlock();
        }
    }, [pin, requiredPinLength, loading, handleUnlock]);

    // Keyboard support - capture sur le document entier
    useEffect(() => {
        if (!isLocked) return;

        const handleKeyDown = (e) => {
            // Empêcher la propagation aux éléments derrière
            e.stopPropagation();
            
            if (e.key >= '0' && e.key <= '9') {
                e.preventDefault();
                handleDigitClick(e.key);
            } else if (e.key === 'Backspace') {
                e.preventDefault();
                handleBackspace();
            } else if (e.key === 'Enter') {
                e.preventDefault();
                handleUnlock();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                handleClear();
            }
        };

        // Capturer sur la phase de capture pour intercepter avant tout
        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [isLocked, handleDigitClick, handleBackspace, handleUnlock, handleClear]);

    if (!isLocked) return null;

    return (
        <div 
            ref={containerRef}
            tabIndex={-1}
            className="fixed inset-0 flex items-center justify-center"
            style={{ 
                zIndex: 99999,
                isolation: 'isolate',
                pointerEvents: 'auto'
            }}
            data-testid="pos-lock-screen"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
        >
            {/* Backdrop opaque qui bloque tout */}
            <div 
                className="absolute inset-0 bg-emerald-900/95 backdrop-blur-sm"
                style={{ pointerEvents: 'auto' }}
                onClick={(e) => e.stopPropagation()}
            />
            
            {/* Lock screen content */}
            <div 
                className="relative w-full max-w-sm mx-4"
                style={{ 
                    zIndex: 100000,
                    pointerEvents: 'auto' 
                }}
            >
                <div className="bg-white rounded-3xl shadow-2xl p-8 text-center">
                    {/* Lock icon */}
                    <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                        <Lock className="w-10 h-10 text-white" />
                    </div>

                    {/* Title */}
                    <h2 className="text-2xl font-bold text-gray-800 mb-1">
                        Caisse verrouillée
                    </h2>
                    
                    {/* User info */}
                    <div className="flex items-center justify-center gap-2 text-gray-500 mb-2">
                        <User className="w-4 h-4" />
                        <span className="font-medium">{user?.name || user?.username || 'Utilisateur'}</span>
                    </div>

                    {/* Inactivity message */}
                    {lockedByInactivity && (
                        <p className="text-sm text-amber-600 mb-4 bg-amber-50 py-2 px-3 rounded-lg">
                            Verrouillée automatiquement après inactivité
                        </p>
                    )}

                    {/* PIN instruction */}
                    <p className="text-gray-600 mb-6">
                        Entrez votre PIN pour déverrouiller
                    </p>

                    {/* PIN display */}
                    <div className="flex justify-center gap-2 mb-6">
                        {[...Array(6)].map((_, i) => (
                            <div
                                key={i}
                                className={cn(
                                    "w-10 h-12 rounded-lg border-2 flex items-center justify-center text-2xl font-bold transition-all",
                                    i < pin.length 
                                        ? "border-emerald-500 bg-emerald-50 text-emerald-600" 
                                        : "border-gray-200 bg-gray-50"
                                )}
                            >
                                {i < pin.length ? '●' : ''}
                            </div>
                        ))}
                    </div>

                    {/* Error message */}
                    {error && (
                        <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-red-600 text-sm font-medium">
                                {error}
                            </p>
                        </div>
                    )}

                    {/* PIN Keypad */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                            <button
                                key={digit}
                                type="button"
                                className={cn(
                                    "h-14 text-2xl font-bold rounded-xl border-2 transition-all",
                                    "bg-white text-gray-800 border-gray-300",
                                    "hover:bg-gray-100 hover:border-gray-400",
                                    "active:bg-gray-200 active:scale-95",
                                    "focus:outline-none focus:ring-2 focus:ring-emerald-500",
                                    loading && "opacity-50 cursor-not-allowed"
                                )}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!loading) handleDigitClick(String(digit));
                                }}
                                disabled={loading}
                            >
                                {digit}
                            </button>
                        ))}
                        <button
                            type="button"
                            className={cn(
                                "h-14 text-lg font-bold rounded-xl border-2 transition-all",
                                "bg-white text-gray-500 border-gray-300",
                                "hover:bg-gray-100",
                                "active:scale-95",
                                "focus:outline-none focus:ring-2 focus:ring-gray-400",
                                loading && "opacity-50 cursor-not-allowed"
                            )}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!loading) handleClear();
                            }}
                            disabled={loading}
                        >
                            C
                        </button>
                        <button
                            type="button"
                            className={cn(
                                "h-14 text-2xl font-bold rounded-xl border-2 transition-all",
                                "bg-white text-gray-800 border-gray-300",
                                "hover:bg-gray-100 hover:border-gray-400",
                                "active:bg-gray-200 active:scale-95",
                                "focus:outline-none focus:ring-2 focus:ring-emerald-500",
                                loading && "opacity-50 cursor-not-allowed"
                            )}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!loading) handleDigitClick('0');
                            }}
                            disabled={loading}
                        >
                            0
                        </button>
                        <button
                            type="button"
                            className={cn(
                                "h-14 text-xl font-bold rounded-xl border-2 transition-all",
                                "bg-white text-red-500 border-gray-300",
                                "hover:bg-red-50 hover:border-red-300 hover:text-red-600",
                                "active:scale-95",
                                "focus:outline-none focus:ring-2 focus:ring-red-400",
                                loading && "opacity-50 cursor-not-allowed"
                            )}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (!loading) handleBackspace();
                            }}
                            disabled={loading}
                        >
                            ⌫
                        </button>
                    </div>

                    {/* Unlock button */}
                    <button
                        type="button"
                        className={cn(
                            "w-full h-12 text-lg font-semibold rounded-xl transition-all",
                            "bg-emerald-600 text-white",
                            "hover:bg-emerald-700",
                            "active:scale-[0.98]",
                            "focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2",
                            "disabled:opacity-50 disabled:cursor-not-allowed",
                            "flex items-center justify-center gap-2"
                        )}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleUnlock();
                        }}
                        disabled={loading || pin.length < requiredPinLength}
                    >
                        {loading ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Vérification...
                            </>
                        ) : (
                            <>
                                <Unlock className="w-5 h-5" />
                                Déverrouiller
                            </>
                        )}
                    </button>

                    {/* Admin bypass option */}
                    {isAdmin && (
                        <div className="mt-6 pt-4 border-t border-gray-100">
                            {!showAdminBypass ? (
                                <button
                                    type="button"
                                    className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowAdminBypass(true);
                                    }}
                                >
                                    Accès administrateur
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className={cn(
                                        "w-full h-10 rounded-xl border-2 transition-all",
                                        "bg-white text-purple-600 border-purple-200",
                                        "hover:bg-purple-50",
                                        "active:scale-[0.98]",
                                        "focus:outline-none focus:ring-2 focus:ring-purple-400",
                                        "disabled:opacity-50",
                                        "flex items-center justify-center gap-2"
                                    )}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleAdminBypass();
                                    }}
                                    disabled={loading}
                                >
                                    <Shield className="w-4 h-4" />
                                    Déverrouiller en tant qu'Admin
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Time display */}
                <div className="text-center mt-4 text-white/70 text-sm font-mono">
                    {new Date().toLocaleTimeString('fr-FR', { 
                        hour: '2-digit', 
                        minute: '2-digit'
                    })}
                </div>
            </div>
        </div>
    );
};

export default POSLockScreen;
